import { Divider, VStack } from '@wegroup/design-system';
import { format, isValid, parseISO } from 'date-fns';
import React, { ReactNode } from 'react';
import { PartyType } from '../../../../../../../../../../types/SMT';
import { Option } from '../../../../../../../../../design-system/src/content/components/SearchPickerInput';
import { InsurancePolicy } from '../../../../../../../types/AdvisoryReport';
import InsurancePolicyHeaderInfo from './InsurancePolicyHeaderInfo';
import InsurancePolicyHeaderParameters from './InsurancePolicyHeaderParameters';

/** Firefox needs to have a year, that's why we're manually adding it */
export const getExpirationDate = (
  dueDate: string | null | undefined,
): string | undefined => {
  const currentYear = new Date().getFullYear();
  const formattedDate = `${currentYear}-${dueDate}`;
  return dueDate && isValid(parseISO(formattedDate))
    ? format(parseISO(formattedDate), 'dd/MM')
    : undefined;
};

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  insurancePolicy: InsurancePolicy;
  withActions?: boolean;
  partyType: PartyType;
  customRiskObjects?: Option[];
  controlled?: boolean;
  hideAIBanner?: boolean;
  customActions?: ReactNode;
  isTailoredPremium?: boolean;

  // Actions
  onEdit?: (insurancePolicy: InsurancePolicy) => void;
  onRemove?: (id: string, packageId?: string) => void;
}

const InsurancePolicyHeader: React.FC<Props> = ({
  insurancePolicy,
  isOpen,
  partyType,
  onToggle,
  controlled,
  customActions,
  customRiskObjects = [],
  withActions,
  onEdit,
  onRemove,
  hideAIBanner,
}) => {
  const isPackage = !!insurancePolicy.policy_package_id;
  const startDate = insurancePolicy.start_date
    ? format(new Date(insurancePolicy.start_date), 'dd/MM/yyyy')
    : undefined;

  return (
    <VStack
      p="4"
      spacing="2"
      divider={<Divider borderColor="gray.50" />}
      align="stretch"
    >
      <InsurancePolicyHeaderInfo
        isOpen={isOpen}
        onToggle={onToggle}
        withActions={withActions}
        insurancePolicy={insurancePolicy}
        partyType={partyType}
        customRiskObjects={customRiskObjects}
        controlled={controlled}
        onEdit={onEdit}
        onRemove={onRemove}
        hideAIBanner={hideAIBanner}
        customActions={customActions}
      />
      {!isPackage && (
        <InsurancePolicyHeaderParameters
          expirationDate={getExpirationDate(insurancePolicy?.due_date)}
          insuranceCompany={
            insurancePolicy.insurance_company ||
            insurancePolicy.coverages?.[0]?.insurance_company
          }
          policyNumber={insurancePolicy.policy_nr}
          premium={insurancePolicy.premium}
          paymentInterval={insurancePolicy.payment_interval}
          insuredValue={insurancePolicy.insured_value}
          deductable={insurancePolicy.deductible}
          withActions={withActions}
          isOffer={insurancePolicy.type === 'INACTIVE'}
          isSimulation={insurancePolicy.type === 'SIMULATION'}
          startDate={startDate}
          isTailoredPremium={!!insurancePolicy.is_tailored_premium}
        />
      )}
    </VStack>
  );
};

export default InsurancePolicyHeader;
